import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DocumentText, CloseCircle } from "iconsax-react";

interface TermsViewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsViewModal: React.FC<TermsViewModalProps> = ({ isOpen, onClose }) => {
  const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-[#000000cc] p-4"
          onClick={handleBackgroundClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative flex flex-col bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 p-3 border-b md:p-6 border-gorgonzola-blue-50">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center rounded-full size-10 md:size-12 min-w-10 md:min-w-12 bg-gorgonzola-blue-50">
                  <DocumentText className="text-gorgonzola-blue-300 size-5 md:size-6 min-w-5 md:min-w-6" variant="Bold" />
                </div>
                <div>
                  <h2 className="text-base font-inter-bold text-closed-shutter-400">Shaku Free Trial Agreement</h2>
                  <p className="text-2xs text-closed-shutter-200">Version 1.0 • Last Updated: December 2025</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="grid transition-colors duration-300 border rounded place-content-center size-10 border-gorgonzola-blue-50 text-closed-shutter-300 hover:text-gorgonzola-blue-300 hover:border-gorgonzola-blue-200"
              >
                <CloseCircle className="size-6" variant="Broken" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-3 overflow-y-auto md:p-6 custom_scroll">
              <div className="space-y-6 text-xs leading-relaxed text-closed-shutter-300">
                <p className="text-closed-shutter-400 font-inter-medium">
                  This Free Trial Agreement ("Agreement") is between Shaku ("Shaku", "we", "us") and the customer accepting this Agreement ("Customer", "you"). By clicking "I agree" or using the Free
                  Trial, you agree to this Agreement, our Terms of Service, and Privacy Policy.
                </p>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">1. Free Trial</h3>
                  <div className="pl-4 space-y-2">
                    <p>
                      <span className="font-inter-medium">1.1 Scope.</span> Shaku grants Customer a limited, non-exclusive, non-transferable right to access and use the Shaku service (the "Service")
                      for evaluation purposes only during the Free Trial.
                    </p>
                    <p>
                      <span className="font-inter-medium">1.2 Term.</span> The Free Trial is valid for 30 days from activation and includes up to 100 API requests (or as otherwise shown on the sign-up
                      page or order form). Shaku may extend or shorten the Trial by written notice (including email).
                    </p>
                    <p>
                      <span className="font-inter-medium">1.3 No Fees During Trial.</span> No subscription fees are charged during the Free Trial. Standard fees apply only if you choose to upgrade to
                      a paid plan.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">2. Conversion to Paid Plan</h3>
                  <div className="pl-4 space-y-2">
                    <p>
                      <span className="font-inter-medium">2.1</span> At the end of the Free Trial or once the usage limit is reached, access may be suspended unless Customer signs up for a paid plan
                      under Shaku's then-current pricing (e.g., plans starting at $99/month for 400 requests).
                    </p>
                    <p>
                      <span className="font-inter-medium">2.2</span> Shaku will not begin charging paid fees without your explicit action (e.g., completing a payment form or selecting a paid plan).
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">3. Customer Responsibilities</h3>
                  <div className="pl-4 space-y-2">
                    <p>
                      <span className="font-inter-medium">3.1</span> Customer will use the Service in compliance with all applicable laws and will not:
                    </p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>misuse the Service,</li>
                      <li>attempt to reverse engineer,</li>
                      <li>use it to build a competing product.</li>
                    </ul>
                    <p>
                      <span className="font-inter-medium">3.2</span> Customer is responsible for configuring integrations and ensuring data provided to Shaku is accurate.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">4. Data Protection & Privacy</h3>
                  <div className="pl-4 space-y-2">
                    <p>
                      <span className="font-inter-medium">4.1</span> Shaku processes personal information in accordance with its Privacy Policy and applicable privacy laws, including PIPEDA and
                      relevant provincial laws where applicable (e.g., Quebec's Law 25).
                    </p>
                    <p>
                      <span className="font-inter-medium">4.2 Face Blurring.</span> Where images are used, Shaku blurs users' faces on the device before images are sent to our servers to help protect
                      user identity.
                    </p>
                    <p>
                      <span className="font-inter-medium">4.3 Trial Data.</span> At the end of the Trial, Customer may request export of its data within 30 days. After that period, Shaku may anonymize
                      or delete Trial data, subject to legal retention requirements.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">5. Intellectual Property & Feedback</h3>
                  <div className="pl-4 space-y-2">
                    <p>
                      <span className="font-inter-medium">5.1</span> Shaku retains all IP rights in the Service. Customer retains ownership of its data.
                    </p>
                    <p>
                      <span className="font-inter-medium">5.2</span> Customer grants Shaku a non-exclusive, worldwide license to use anonymized and aggregated data and feedback from the Trial to
                      improve the Service.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">6. Confidentiality</h3>
                  <p className="pl-4">Each party agrees to protect the other's confidential information and use it only for evaluation and use of the Service during the Trial.</p>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">7. Public Reference (Optional)</h3>
                  <p className="pl-4">
                    Unless Customer objects in writing, Shaku may reference Customer as a "Trial Customer" (name or logo only) in pitch materials or business plans. No confidential details or metrics
                    will be shared without further consent.
                  </p>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">8. Disclaimer & Limitation of Liability</h3>
                  <div className="pl-4 space-y-2">
                    <p>
                      <span className="font-inter-medium">8.1</span> The Free Trial is provided "as is", without warranties of any kind.
                    </p>
                    <p>
                      <span className="font-inter-medium">8.2</span> To the maximum extent permitted by law, Shaku will not be liable for indirect or consequential damages, and its total liability
                      under this Agreement will not exceed $100 CAD.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">9. Termination</h3>
                  <p className="pl-4">
                    Either party may terminate the Trial at any time by written notice (including email). Upon termination, access to the Service will be disabled, subject to the data export period in
                    Section 4.3.
                  </p>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">10. Governing Law</h3>
                  <p className="pl-4">This Agreement is governed by the laws of the Province of Ontario, Canada, and the federal laws of Canada applicable therein.</p>
                </div>

                <div className="p-4 rounded-lg bg-gorgonzola-blue-50">
                  <p className="text-closed-shutter-400">
                    If you have questions, contact us at:{" "}
                    <a href="mailto:info@Shaku.tech" className="font-inter-medium text-gorgonzola-blue-300 hover:underline">
                      info@Shaku.tech
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end p-3 border-t md:p-6 border-gorgonzola-blue-50">
              <button
                onClick={onClose}
                className="flex items-center justify-center gap-2 px-6 py-3 text-white transition-all duration-300 rounded-lg bg-gorgonzola-blue-300 hover:bg-gorgonzola-blue-400"
              >
                <span className="font-inter-medium">Close</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TermsViewModal;
